// The block document model — the browser half. Chunk 1.6, spec §05b/§18/§19.
//
// A document written in TimeKeep is an ordered array of blocks. This file
// defines what a block is, how its inline text parses, and what makes a
// document valid. supabase/functions/_shared/contractBlocks.ts mirrors it, and
// scripts/test-contract-blocks.mjs runs both against the same fixtures on every
// commit — the editor shows a preview, the server renders the PDF that gets
// signed, and those two disagreeing about a legal document is the failure this
// whole arrangement exists to prevent.
//
// Deliberately NOT a word processor. Blocks flow in order; hierarchy and
// spacing come from named styles, never per-block formatting or coordinates.
// That constraint is what keeps the editor buildable and the PDF output
// consistent — see the spec's "Explicitly not a free-placement canvas".

import { MERGE_TOKENS, SIGNER_SCOPED_TOKENS, resolveMerge, inlineAddress } from './contractFields';

export const DOCUMENT_TOKENS = Object.keys(MERGE_TOKENS);
export { inlineAddress };

/** Every block type the renderer can draw. Anything else is a validation error. */
export const BLOCK_TYPES = {
  heading1:      { label: 'Heading 1',    style: 'h1',   text: true },
  heading2:      { label: 'Heading 2',    style: 'h2',   text: true },
  heading3:      { label: 'Heading 3',    style: 'h3',   text: true },
  paragraph:     { label: 'Paragraph',    style: 'body', text: true },
  bullet_list:   { label: 'Bulleted list', style: 'body', items: true },
  numbered_list: { label: 'Numbered list', style: 'body', items: true },
  page_break:    { label: 'Page break' },
  spacer:        { label: 'Space' },
  divider:       { label: 'Divider line' },
  table:         { label: 'Table', style: 'body', table: true },
  columns:       { label: 'Side by side' },
  options:       { label: 'Pricing options' },
  signature_block: { label: 'Signature block' },
};

export const BLOCK_ORDER = [
  'heading1', 'heading2', 'heading3', 'paragraph', 'bullet_list', 'numbered_list',
  'table', 'columns', 'options', 'signature_block', 'divider', 'spacer', 'page_break',
];

// ── Color ────────────────────────────────────────────────────────────
//
// A color value is either one of the named tones below or a hex value the
// customer typed, so a document can match their branding. Both runtimes
// resolve it here, because the editor's preview is a promise about the PDF.

// no-hex:allow-start
// These are INK ON A PRINTED PAGE, not app chrome. A PDF has no light or dark
// mode and no org accent -- a contract must look the same on every screen and
// on paper -- so these are fixed values shared with the server renderer rather
// than theme tokens. They are the exact colors documents already rendered
// with, so nothing written before colors existed changes appearance.
/** The tones documents used before colors were customizable. */
export const TONES = { ink: '#0D1B2A', muted: '#6B788A', rule: '#CCD6E0' }
// no-hex:allow-end

export function isHexColor(v) {
  return typeof v === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v.trim())
}

/**
 * A color value -> {r, g, b} in 0..1.
 *
 * NEVER THROWS. A contract has to render even if someone typed nonsense into a
 * color box, so anything unrecognized falls back to the named tone rather
 * than failing the send -- a document that will not freeze because of a
 * mistyped color is a far worse failure than a paragraph in the wrong black.
 */
export function docColor(value, fallback = 'ink') {
  const raw = isHexColor(value) ? String(value).trim()
    : (TONES[String(value)] || TONES[fallback] || TONES.ink)
  let h = raw.slice(1)
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
  const n = parseInt(h, 16)
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 }
}

/** The same value as a CSS color, for the editor's preview. */
export function docColorCss(value, fallback = 'ink') {
  if (isHexColor(value)) return String(value).trim()
  return TONES[String(value)] || TONES[fallback] || TONES.ink
}

export const STYLE_NAMES = ['h1', 'h2', 'h3', 'body', 'caption', 'quote'];

/** How a block's text is filled. One field is what turns a document into a template. */
export const FILL_MODES = {
  fixed:    { label: 'Same every time', hint: 'Locked text — identical on every contract.' },
  prompted: { label: 'Filled when sending', hint: 'You type this for each contract.' },
};

// Sizes are points. Colors are NAMES, resolved by whichever renderer draws
// them — the PDF renderer maps them to ink, the editor preview to theme
// colors. No hex lives in the document model.
export const DEFAULT_STYLES = {
  h1:      { size: 18,   bold: true,  space_before: 16, space_after: 8,  color: 'ink' },
  h2:      { size: 14,   bold: true,  space_before: 14, space_after: 6,  color: 'ink' },
  h3:      { size: 11.5, bold: true,  space_before: 12, space_after: 4,  color: 'ink' },
  body:    { size: 10.5, line: 1.45,  space_after: 9,                    color: 'ink' },
  caption: { size: 9,    line: 1.35,  space_after: 6,                    color: 'muted' },
  quote:   { size: 10.5, italic: true, line: 1.45, indent: 24, space_after: 9, color: 'muted' },
  // Not a paragraph style, so deliberately absent from STYLE_NAMES: it only
  // carries the default color of a divider line.
  divider: { color: 'rule' },
};

export const DEFAULT_LAYOUT = {
  paper: 'letter',
  margins: { top: 72, right: 72, bottom: 72, left: 72 },
  // header_image.source: 'org' uses the logo already on the organization,
  // 'upload' uses a letterhead uploaded for documents (path is in the
  // contract-documents bucket), 'none' draws nothing. Default 'org' is what
  // every existing document already did.
  logo: { show: true, width: 120, width_pct: null, repeat: false, source: 'org', path: null, align: 'left' },
  header_text: '',
  // Page 1 is allowed to differ: 'same' | 'different' (header_text_first) |
  // 'none'. A cover page overrides all of it and shows no chrome at all.
  header_first_mode: 'same',
  header_text_first: '',
  cover_page: false,
  footer_text: '',
  page_numbers: true,
  // Off by default: turning it on under an existing document would renumber
  // wording someone already typed numbers into.
  clause_numbering: { enabled: false, start_level: 'h2' },
};

const TOKEN_RE = /\{\{[^{}]*\}\}/g;

/**
 * What {{effective_date}} says in a document that takes effect when it is
 * signed. The document is frozen at send; the real date is only known when the
 * last signature lands, so printing today's date would state something that
 * may well be false by the time anyone signs. Describing the date instead is
 * true the moment it is written and stays true.
 *
 * To print the actual date on the page, place an Effective date field — that
 * is stamped onto the executed copy once the date is known.
 */
export const EFFECTIVE_ON_SIGNATURE_TEXT = 'the date this agreement is signed';

/**
 * Resolve a token for DOCUMENT BODY text. Same values as anywhere else, with
 * the one override above. Mirrored in _shared/contractBlocks.ts, so the
 * editor's preview and the rendered PDF say the same thing.
 */
export function resolveDocumentToken(token, ctx) {
  if (token === '{{effective_date}}'
      && ctx?.contract?.effective_on_signature && !ctx?.contract?.effective_date) {
    return EFFECTIVE_ON_SIGNATURE_TEXT;
  }
  return resolveMerge(token, ctx);
}

// A token that resolves to nothing takes the punctuation holding its place
// with it: "{{address_1}}, {{address_2}}" with no second line should read
// "1 Cherry Tree Ln", not "1 Cherry Tree Ln,".
const SEP_BEFORE = /[,;\u00b7]\s*$/;
const SEP_AFTER = /^\s*[,;\u00b7]\s*/;

/**
 * Block text -> the lines to draw, with merge tokens resolved.
 *
 * Three rules, all aimed at one failure: a legal document should never show
 * the shape of a detail it does not have.
 *  1. A typed line break IS a line break. (Without this, someone laying an
 *     address out over two lines gets one run-on line.)
 *  2. A token that resolves to nothing swallows one adjacent separator.
 *  3. A line left with nothing but whitespace is dropped, rather than
 *     printing a blank gap where a detail was meant to be.
 *
 * `onBlank` is called for each token that had no value, so the renderer can
 * warn about it -- a line quietly disappearing is worse than one that stays.
 */
export function resolveLines(text, resolve, onBlank, isDeferred) {
  const out = [];
  for (const rawLine of String(text ?? '').split('\n')) {
    const runs = parseInline(rawLine);
    const resolved = [];
    let blanked = false;

    for (const run of runs) {
      if (!run.token) { resolved.push({ ...run }); continue; }
      // Known only once the document is signed: hold a gap open for it rather
      // than describing it in prose or dropping the line.
      if (isDeferred && isDeferred(run.token)) {
        resolved.push({ ...run, text: '', blank: true });
        continue;
      }
      const value = resolve ? resolve(run.token) : null;
      if (value == null || String(value).trim() === '') {
        blanked = true;
        if (onBlank) onBlank(run.token);
        resolved.push({ ...run, text: '' });
      } else {
        resolved.push({ ...run, text: String(value) });
      }
    }

    if (blanked) {
      resolved.forEach((run, i) => {
        if (run.text !== '' || !run.token || run.blank) return;
        // Look back for the separator that was joining this to the last value.
        for (let j = i - 1; j >= 0; j--) {
          if (resolved[j].text === '') continue;
          if (SEP_BEFORE.test(resolved[j].text)) {
            resolved[j] = { ...resolved[j], text: resolved[j].text.replace(SEP_BEFORE, '') };
            return;
          }
          break;
        }
        // Nothing before it, so take the one after instead.
        for (let j = i + 1; j < resolved.length; j++) {
          if (resolved[j].text === '') continue;
          if (SEP_AFTER.test(resolved[j].text)) {
            resolved[j] = { ...resolved[j], text: resolved[j].text.replace(SEP_AFTER, '') };
          }
          return;
        }
      });
    }

    const kept = resolved.filter((r) => r.text !== '' || r.blank);
    if (kept.some((r) => r.blank || r.text.trim() !== '')) out.push(kept);
    else if (!blanked && rawLine.trim() === '' && out.length) out.push([]);  // a deliberate blank line
  }
  return out;
}

let seq = 0;
/** Stable ids matter: contract_fields anchors to block_id. */
export function newBlockId() {
  seq += 1;
  return `b${Date.now().toString(36)}${seq.toString(36)}`;
}

export function newBlock(type, over = {}) {
  const def = BLOCK_TYPES[type] || BLOCK_TYPES.paragraph;
  const block = { id: newBlockId(), type, fill_mode: 'fixed' };
  if (def.style) block.style = def.style;
  if (def.text) block.text = '';
  if (def.items) block.items = [''];
  if (type === 'signature_block') {
    block.label = 'Signatures';
    // Two columns by default, because a signature page normally reads as one
    // party per side. The headers are the "For Acme Corp" line above each.
    block.columns = 2;
    block.column_headers = ['', ''];
  };
  if (type === 'table') {
    // A small table that already looks like a table, so the first thing you see
    // is the shape rather than an empty frame you have to configure.
    block.header_row = true;
    block.rows = [['', ''], ['', ''], ['', '']];
    block.col_widths = null;        // null = equal columns
    block.header_fill = 'rule';
    block.stripe_fill = null;
    block.border_color = 'rule';
  }
  if (type === 'columns') {
    // Two empty columns, so the shape is visible before anything is put in it.
    block.cols = [[], []];
    block.gap = 18;
    block.col_widths = null;
  }
  if (type === 'options') {
    // A heading above the cards. The options themselves live in their own
    // table and are rendered from there, so this block holds no wording of
    // its own -- editing an option must not mean editing the document.
    block.text = 'Pricing';
  }
  if (type === 'spacer') block.height = 18;
  return { ...block, ...over };
}

/**
 * Inline markup: **bold**, *italic*, and {{merge_tokens}} — a small, closed
 * subset rather than a rich-text editor, so the PDF renderer stays tractable.
 * Returns runs in order. An unclosed marker keeps its emphasis to the end of
 * the text rather than being dropped: deterministic, and visible to the author
 * in the preview.
 */
export function parseInline(text) {
  const src = String(text ?? '');
  const runs = [];
  let last = 0;
  TOKEN_RE.lastIndex = 0;
  let m;
  while ((m = TOKEN_RE.exec(src)) !== null) {
    if (m.index > last) emphasisRuns(src.slice(last, m.index), runs);
    runs.push({ text: m[0], token: m[0], bold: false, italic: false });
    last = m.index + m[0].length;
  }
  if (last < src.length) emphasisRuns(src.slice(last), runs);
  return runs;
}

function emphasisRuns(segment, runs) {
  let bold = false;
  let italic = false;
  let buf = '';
  const flush = () => {
    if (buf) runs.push({ text: buf, bold, italic, token: null });
    buf = '';
  };
  for (let i = 0; i < segment.length;) {
    if (segment.startsWith('**', i)) { flush(); bold = !bold; i += 2; continue; }
    if (segment[i] === '*') { flush(); italic = !italic; i += 1; continue; }
    buf += segment[i];
    i += 1;
  }
  flush();
}

/** The block's text with markers removed and tokens left in place. */
/**
 * A table's rows as a rectangle: every row padded to the widest, never
 * truncated. A ragged `rows` array is easy to produce by editing and must not
 * silently drop a cell of an agreement.
 */
export function tableGrid(block) {
  const rows = Array.isArray(block?.rows) ? block.rows : [];
  const cols = Math.max(1, ...rows.map((r) => (Array.isArray(r) ? r.length : 0)));
  return rows.map((r) => {
    const row = Array.isArray(r) ? r.slice(0, cols) : [];
    while (row.length < cols) row.push('');
    return row.map((c) => String(c ?? ''));
  });
}

/** Column widths as fractions that sum to 1, whatever was stored. */
export function tableColumnWidths(block, cols) {
  const raw = Array.isArray(block?.col_widths) ? block.col_widths : null;
  const n = Math.max(1, cols);
  if (!raw || raw.length !== n) return Array.from({ length: n }, () => 1 / n);
  const nums = raw.map((w) => (Number(w) > 0 ? Number(w) : 0));
  const total = nums.reduce((a, b) => a + b, 0);
  if (!(total > 0)) return Array.from({ length: n }, () => 1 / n);
  return nums.map((w) => w / total);
}

// ── Clause numbering and cross-references ─────────────────────────────
//
// Contracts are argued about by number. "as described in Section 6.1" has to
// keep pointing at the right clause after someone inserts a paragraph above it,
// and hand-typed numbers do not: one reorder and every reference in the
// document is quietly wrong. So numbers are DERIVED from position, and a
// reference stores the id of the section it points at, never the number.

/** Which heading styles can be numbered, outermost first. */
export const HEADING_LEVELS = ['h1', 'h2', 'h3']

/** The style a block renders as, resolving both ways of writing a heading. */
export function blockStyleName(block) {
  if (!block) return 'body'
  if (block.style) return block.style
  return String(block.type || '').startsWith('heading') ? `h${String(block.type).slice(-1)}` : 'body'
}

export function isRefToken(token) {
  return typeof token === 'string' && /^\{\{ref:[A-Za-z0-9_-]+\}\}$/.test(token)
}

export function refTargetId(token) {
  const m = /^\{\{ref:([A-Za-z0-9_-]+)\}\}$/.exec(String(token || ''))
  return m ? m[1] : null
}

export function refToken(blockId) {
  return `{{ref:${blockId}}}`
}

/**
 * Every numbered section's number, keyed by block id.
 *
 * Numbering starts at `start_level` because the document's own title is almost
 * always the one H1 on the page and must not become "1." A section can also opt
 * out individually (`unnumbered`), which is what a "Signatures" heading wants.
 */
export function computeClauseNumbers(blocks, layout) {
  const cfg = (layout && layout.clause_numbering) || {}
  const out = {}
  if (!cfg.enabled) return out
  const start = HEADING_LEVELS.indexOf(cfg.start_level || 'h2')
  const levels = HEADING_LEVELS.slice(start < 0 ? 1 : start)
  const counters = levels.map(() => 0)
  // Columns are walked through in place, so a heading inside one is numbered
  // exactly where it sits in the document.
  for (const b of flattenBlocks(blocks)) {
    if (!b || b.unnumbered) continue
    const depth = levels.indexOf(blockStyleName(b))
    if (depth < 0) continue
    counters[depth] += 1
    for (let d = depth + 1; d < counters.length; d++) counters[d] = 0
    out[b.id] = counters.slice(0, depth + 1).join('.')
  }
  return out
}

/**
 * How the number reads at the head of its own clause. A top-level clause takes
 * a full stop ("1."), a sub-clause does not ("6.1") -- which is how legal
 * documents are written, and how Austin's own MSA was typed by hand.
 */
export function clauseNumberPrefix(number) {
  if (!number) return ''
  return String(number).includes('.') ? `${number} ` : `${number}. `
}

// ── Columns ───────────────────────────────────────────────────────────
//
// Side by side, as an explicit container rather than free-floating widths.
//
// "Any section can be under 100% wide and two of them flow side by side" is a
// wrapping layout engine, and in a document that paginates that is where the
// bugs that misplace a signature live. A columns container has one answer to
// every question a page break can ask, which free widths do not.
//
// Deliberately NOT allowed inside a column: another columns block (no nesting
// to reason about), a signature block (it already lays out its own columns, and
// its fields are anchored at freeze), and a page break (a page cannot break
// inside half a row).

export const COLUMN_CHILD_TYPES = [
  'paragraph', 'heading1', 'heading2', 'heading3',
  'bullet_list', 'numbered_list', 'table', 'divider', 'spacer',
]

/** Every column of a columns block, as arrays of blocks. */
export function columnsOf(block) {
  const cols = Array.isArray(block?.cols) ? block.cols : []
  return cols.map((c) => (Array.isArray(c) ? c.filter(Boolean) : []))
}

/**
 * Every block in document order, columns walked through in place.
 *
 * This is what keeps the rest of the model flat: numbering, validation, plain
 * text and field lookup all read the document through here, so a section inside
 * a column is numbered, validated and searched exactly like one outside it.
 */
export function flattenBlocks(blocks) {
  const out = []
  for (const b of blocks || []) {
    if (!b) continue
    out.push(b)
    if (b.type === 'columns') {
      for (const col of columnsOf(b)) for (const child of col) out.push(child)
    }
  }
  return out
}

/** Replace a block anywhere in the tree, including inside a column. */
export function replaceBlock(blocks, id, patch) {
  return (blocks || []).map((b) => {
    if (!b) return b
    if (b.id === id) return { ...b, ...patch }
    if (b.type === 'columns') {
      return {
        ...b,
        cols: columnsOf(b).map((col) => col.map((c) => (c.id === id ? { ...c, ...patch } : c))),
      }
    }
    return b
  })
}

/**
 * A copy of a block, with a FRESH ID FOR EVERY BLOCK IN IT, nested ones
 * included, and no array shared with the original.
 *
 * Ids are how every other operation finds a block: `replaceBlock` patches
 * EVERY block whose id matches, anywhere in the tree, and `removeBlock`
 * deletes every match. So a duplicate that reuses an id is not merely
 * untidy -- editing the copy silently edits the original too, and deleting
 * one deletes both. Austin hit exactly that: duplicating a Side by side gave
 * the container a new id but left its children sharing theirs, so retyping
 * the copy's first heading rewrote the original's.
 *
 * Arrays are copied rather than referenced for the same reason: two blocks
 * pointing at one `rows` or `items` array is the same bug a level down.
 *
 * contract_fields anchor to block_id, so fields are deliberately NOT copied --
 * one signature cannot be in two places.
 */
export function cloneBlockTree(block) {
  if (!block) return block;
  const copy = { ...block, id: newBlockId() };
  if (Array.isArray(block.items)) copy.items = [...block.items];
  if (Array.isArray(block.column_headers)) copy.column_headers = [...block.column_headers];
  if (Array.isArray(block.col_widths)) copy.col_widths = [...block.col_widths];
  if (Array.isArray(block.rows)) copy.rows = block.rows.map((r) => (Array.isArray(r) ? [...r] : r));
  if (block.type === 'columns') copy.cols = columnsOf(block).map((col) => col.map(cloneBlockTree));
  return copy;
}

/** Remove a block anywhere in the tree. */
export function removeBlock(blocks, id) {
  return (blocks || [])
    .filter((b) => b && b.id !== id)
    .map((b) => (b.type === 'columns'
      ? { ...b, cols: columnsOf(b).map((col) => col.filter((c) => c.id !== id)) }
      : b))
}

export function blockPlainText(block) {
  if (!block) return '';
  if (block.type === 'options') {
    return String(block.text ?? '')
  }
  if (block.type === 'columns') {
    return columnsOf(block).map((col) => col.map(blockPlainText).filter(Boolean).join('\n')).join('\n')
  }
  if (block.type === 'table') {
    return tableGrid(block).map((row) => row.map((c) => parseInline(c).map((r) => r.text).join('')).join('\t')).join('\n')
  }
  if (Array.isArray(block.items)) {
    return block.items.map((it) => parseInline(it).map((r) => r.text).join('')).join('\n');
  }
  return parseInline(block.text).map((r) => r.text).join('');
}

/**
 * Tokens whose value is not knowable when the document is frozen, but will be
 * the moment it is signed. They are drawn as a blank in the sent document and
 * stamped on the executed copy -- the way a printed contract leaves a gap for
 * the date rather than describing it in words.
 *
 * Today that is the effective date of an agreement that takes effect on
 * signature. Nothing else qualifies: every other token is either known at
 * freeze or belongs to a specific signer, and a signer token in body text has
 * no single answer anyway.
 */
export function isDeferredToken(token, ctx) {
  return token === '{{effective_date}}'
    && !!ctx?.contract?.effective_on_signature
    && !ctx?.contract?.effective_date;
}

export function documentPlainText(blocks) {
  return (blocks || []).map(blockPlainText).filter(Boolean).join('\n\n');
}

export function promptedBlocks(blocks) {
  return flattenBlocks(blocks).filter((b) => b.fill_mode === 'prompted');
}

/**
 * The document as it will actually render: prompted blocks replaced by what
 * the sender typed (contracts.field_values), falling back to the block's
 * default text. Pure — the caller decides what to do with the result.
 */
export function applyFieldValues(blocks, values = {}) {
  const fill = (b) => {
    if (b?.type === 'columns') {
      return { ...b, cols: columnsOf(b).map((col) => col.map(fill)) };
    }
    if (b.fill_mode !== 'prompted') return b;
    const typed = values?.[b.id];
    const text = typed != null && String(typed).trim() !== ''
      ? String(typed)
      : (b.default_text ?? '');
    return Array.isArray(b.items) ? { ...b, items: String(text).split('\n') } : { ...b, text };
  };
  return (blocks || []).map(fill);
}

/**
 * Errors block sending; warnings do not. "Dear ," in an executed legal document
 * is worth a hard gate, but a missing optional address is not.
 */
export function validateBlocks(blocks, { values = null, layout = null } = {}) {
  // Cross-references are checked against the numbering this document actually
  // produces, so a reference to a section that was deleted -- or that stopped
  // being numbered -- is caught here rather than printed as a hole.
  const numbers = computeClauseNumbers(blocks, layout || DEFAULT_LAYOUT);
  const errors = [];
  const warnings = [];
  const list = flattenBlocks(blocks);
  const seen = new Set();

  if (list.length === 0) errors.push({ code: 'empty_document', message: 'The document has no content yet.' });

  list.forEach((b, i) => {
    const where = `Block ${i + 1}`;
    if (!b || !b.id) {
      errors.push({ code: 'missing_id', message: `${where} has no id.` });
      return;
    }
    if (seen.has(b.id)) errors.push({ code: 'duplicate_id', blockId: b.id, message: `${where} repeats an id.` });
    seen.add(b.id);

    if (!BLOCK_TYPES[b.type]) {
      errors.push({ code: 'unknown_type', blockId: b.id, message: `${where} is a kind TimeKeep can’t draw (${b.type}).` });
      return;
    }
    if (b.style && !STYLE_NAMES.includes(b.style)) {
      errors.push({ code: 'unknown_style', blockId: b.id, message: `${where} uses an unknown style (${b.style}).` });
    }
    if (b.fill_mode && !FILL_MODES[b.fill_mode]) {
      errors.push({ code: 'unknown_fill_mode', blockId: b.id, message: `${where} has an unknown fill mode.` });
    }

    const def = BLOCK_TYPES[b.type];
    const text = blockPlainText(b);
    if (b.fill_mode === 'prompted') {
      if (!String(b.label ?? '').trim()) {
        errors.push({ code: 'prompted_missing_label', blockId: b.id, message: `${where} is filled when sending but has no label, so nobody knows what to type.` });
      }
      if (values) {
        const filled = String(values?.[b.id] ?? b.default_text ?? '').trim();
        if (b.required && !filled) {
          errors.push({ code: 'missing_required_value', blockId: b.id, message: `“${b.label || where}” still needs to be filled in.` });
        }
      }
    } else if (b.type === 'columns') {
      const cols = columnsOf(b)
      if (cols.length < 2) {
        errors.push({ code: 'columns_too_few', blockId: b.id, message: `${where} is side-by-side but has fewer than two columns.` })
      }
      for (const col of cols) {
        for (const child of col) {
          if (!COLUMN_CHILD_TYPES.includes(child?.type)) {
            errors.push({ code: 'bad_column_child', blockId: b.id,
              message: `${where} has a ${BLOCK_TYPES[child?.type]?.label || child?.type} inside a column, which cannot go there.` })
          }
        }
      }
      if (cols.every((c) => c.length === 0)) {
        warnings.push({ code: 'empty_block', blockId: b.id, message: `${where} is side-by-side with nothing in it.` })
      }
    } else if (def.table) {
      // A table's words are still the agreement's words: they go through the
      // same token checks, and an empty one is worth saying out loud.
      const grid = tableGrid(b)
      if (grid.length === 0) {
        errors.push({ code: 'empty_table', blockId: b.id, message: `${where} is a table with no rows.` })
      } else if (!text.replace(/[\t\n]/g, '').trim()) {
        warnings.push({ code: 'empty_block', blockId: b.id, message: `${where} is a table with nothing in it.` })
      }
    } else if ((def.text || def.items) && !text.trim()) {
      warnings.push({ code: 'empty_block', blockId: b.id, message: `${where} is empty.` });
    }

    const cells = b.type === 'table' ? tableGrid(b).flat() : [];
    for (const run of parseInline(b.text ?? '').concat(
      ...(b.items || []).map(parseInline), ...cells.map(parseInline))) {
      if (!run.token) continue;
      if (isRefToken(run.token)) {
        const target = refTargetId(run.token)
        if (!numbers[target]) {
          errors.push({ code: 'dangling_reference', blockId: b.id, token: run.token,
            message: `${where} points at a section that is no longer numbered, or no longer there. Re-pick the cross-reference.` })
        }
      } else if (!DOCUMENT_TOKENS.includes(run.token)) {
        errors.push({ code: 'unknown_token', blockId: b.id, token: run.token,
          message: `${run.token} isn’t a field TimeKeep can fill in.` });
      } else if (SIGNER_SCOPED_TOKENS.has(run.token)) {
        // A document can have several signers, so in body text these have no
        // single answer and render blank. In a field they are fine: a field
        // belongs to one signer.
        warnings.push({ code: 'signer_token_in_body', blockId: b.id, token: run.token,
          message: `${run.token} is left blank in the document text, because a contract can have more than one signer. Use it in a field instead.` });
      }
    }
  });

  return { errors, warnings, ok: errors.length === 0 };
}

/** A starter document, so a new contract isn't a blank page. */
/**
 * A cover page, as ordinary blocks followed by a page break. Nothing about it
 * is a special kind of page -- the only thing that makes it a cover is the
 * `cover_page` layout setting, which keeps the running header, footer and
 * page number off it. So it can be edited, reordered or deleted like any
 * other section.
 */
export function coverBlocks() {
  return [
    newBlock('spacer', { height: 90 }),
    newBlock('heading1', { text: '{{contract_title}}' }),
    newBlock('paragraph', { style: 'caption', text: '{{contract_number}}' }),
    newBlock('spacer', { height: 40 }),
    newBlock('paragraph', { text: '**{{org_name}}**' }),
    newBlock('paragraph', { style: 'caption', text: '{{org_address}}' }),
    newBlock('spacer', { height: 18 }),
    newBlock('paragraph', { text: '**{{counterparty_name}}**' }),
    newBlock('paragraph', { style: 'caption', text: '{{counterparty_address}}' }),
    newBlock('spacer', { height: 40 }),
    newBlock('paragraph', { style: 'caption', text: 'Effective {{effective_date}}' }),
    newBlock('page_break'),
  ];
}

export function starterBlocks() {
  return [
    newBlock('heading1', { text: '{{contract_title}}' }),
    newBlock('paragraph', { text: 'This agreement is made between **{{org_name}}** and **{{counterparty_name}}**, effective {{effective_date}}.' }),
    newBlock('heading2', { text: 'Scope of services' }),
    newBlock('paragraph', {
      fill_mode: 'prompted', label: 'Scope of services',
      hint: 'Describe the services provided. Be specific about deliverables and exclusions.',
      required: true, placeholder: 'Acme Consulting will provide…',
    }),
    newBlock('signature_block'),
  ];
}
